import { mapAppGroupSearchEntryToOptions } from '@openx/components-targeting/Targeting/AppInventory/utils/appGroupSearchOptions.ts';
import { DEFAULT_SEARCH_DEBOUNCE_MS } from '@openx/constants/search.ts';
import type { AppGroup } from '@openx/types/targeting/inventoryAndContent.ts';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useDebouncedValue } from '@openx/utils/hooks/useDebouncedValue.ts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { AppGroupSearchDocument } from '../../../../graphql/query/options/AppGroupSearch.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../../modules/graphql/types.ts';

const MIN_SEARCH_LENGTH = 2;
const SUGGESTION_LIMIT = 50;

export const useAppInventoryFetch: NonNullable<TargetingProviderConfig['useAppInventoryFetch']> = ({
  searchPhrase,
  enabled = true,
}) => {
  const debouncedSearchPhrase = useDebouncedValue(searchPhrase, DEFAULT_SEARCH_DEBOUNCE_MS);

  const shouldSearch = enabled && debouncedSearchPhrase.length >= MIN_SEARCH_LENGTH;

  const variables = { names: [debouncedSearchPhrase], suggestion_limit: SUGGESTION_LIMIT };
  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.SEARCH, 'appgroup', debouncedSearchPhrase, variables],
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(AppGroupSearchDocument, variables),
    enabled: shouldSearch,
  });

  const result = useMemo<AppGroup[]>(() => {
    if (!shouldSearch) return [];

    const entry = data?.appGroupSearch?.[0];
    return mapAppGroupSearchEntryToOptions(
      entry ? { match: entry.match ?? null, suggestions: entry.suggestions } : undefined,
      debouncedSearchPhrase
    );
  }, [data, shouldSearch, debouncedSearchPhrase]);

  return {
    data: result,
    isLoading: loading,
    refetch,
  };
};
